<template>
    <div>
        <div class="btn-group mb-3">
            <button
                v-for="y in years"
                :key="'btn-' + y"
                type="button"
                class="btn btn-light"
                :class="{ 'active': year === y }"
                @click="year = y"
            >
                {{ y }}
            </button>
        </div>
        <div class="mb-3">
            <v-chart style="height: 400px;" v-if="chart_options.series.length > 0" class="chart" :option="chart_options" :update-options="{ replaceMerge: ['xAxis', 'yAxis', 'series'] }" autoresize/>
        </div>
        <div class="row">
            <div class="col-xl-3">
                <div class="card">
                    <div class="card-header bg-white">
                        <h5 class="card-title">Önskade leveranser {{ year }}</h5>
                    </div>
                    <div v-if="deliveries || busy">
                        <table class="table table-xs">
                            <thead>
                                <tr>
                                    <th>Vecka</th>
                                    <th class="text-right">Inlösen</th>
                                </tr>
                            </thead>
                            <tbody v-if="busy">
                                <tr>
                                    <td colspan="2" class="text-center py-5"><i class="icon-spinner2 spinner"></i></td>
                                </tr>
                            </tbody>
                            <tbody v-else-if="deliveries && deliveries.wanted_deliveries && Object.keys(deliveries.wanted_deliveries).length > 0">
                                <tr v-for="(item, week) in deliveries.wanted_deliveries" :key="'w-' + week" :class="{ 'text-muted': item.antal === 0 }">
                                    <td>
                                        <a
                                            v-if="item.antal > 0"
                                            :href="'/batch/list?type=prod_order&prod_id=' + id + '&date_from=' + item.wanted_delivery_date_start + '&date_to=' + item.wanted_delivery_date_stop"
                                            class="font-weight-bold"
                                        >
                                            Vecka {{ week }}
                                        </a>
                                        <span v-else>Vecka {{ week }}</span>
                                    </td>
                                    <td class="text-right" :class="{ 'font-weight-bold': (item.antal > 0) }">{{ item.antal }}</td>
                                </tr>
                            </tbody>
                            <tbody v-else>
                                <tr>
                                    <td colspan="2" class="text-center py-5"><i>Ingen data...</i></td>
                                </tr>
                            </tbody>
                            <tfoot v-if="deliveries && deliveries.wanted_deliveries_total">
                                <tr>
                                    <td class="font-weight-bold" style="border-top: 1px solid #333;">Totalt</td>
                                    <td class="text-right font-weight-bold" style="border-top: 1px solid #333;">{{ deliveries.wanted_deliveries_total }}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    <div class="card-body text-danger" v-else><i>Ingen data...</i></div>
                </div>
            </div>
            <div class="col-xl-3">
                <div class="card">
                    <div class="card-header bg-white">
                        <h5 class="card-title">Faktiska leveranser {{ year }}</h5>
                    </div>
                    <div v-if="deliveries || busy">
                        <table class="table table-xs">
                            <thead>
                                <tr>
                                    <th>Vecka</th>
                                    <th class="text-right">Inlösen</th>
                                    <th class="text-right">Sälj</th>
                                </tr>
                            </thead>
                            <tbody v-if="busy">
                                <tr>
                                    <td colspan="3" class="text-center py-5"><i class="icon-spinner2 spinner"></i></td>
                                </tr>
                            </tbody>
                            <tbody v-else-if="deliveries && deliveries.actual_deliveries && Object.keys(deliveries.actual_deliveries).length > 0">
                                <tr v-for="(item, week) in deliveries.actual_deliveries" :key="'w-' + week" :class="{ 'text-muted': item.levstat === 0 && item.salestat === 0 }">
                                    <td :class="{ 'font-weight-bold': (item.levstat > 0 || item.salestat > 0) }">Vecka {{ week }}</td>
                                    <td class="text-right" :class="{ 'font-weight-bold': (item.levstat > 0) }">{{ item.levstat }}</td>
                                    <td class="text-right" :class="{ 'font-weight-bold': (item.salestat > 0) }">{{ item.salestat }}</td>
                                </tr>
                            </tbody>
                            <tbody v-else>
                                <tr>
                                    <td colspan="3" class="text-center py-5"><i>Ingen data...</i></td>
                                </tr>
                            </tbody>
                            <tfoot v-if="deliveries && deliveries.actual_deliveries">
                                <tr>
                                    <td class="font-weight-bold" style="border-top: 1px solid #333;">Totalt</td>
                                    <td class="text-right font-weight-bold" style="border-top: 1px solid #333;">{{ deliveries.actual_deliveries_total_lev }}</td>
                                    <td class="text-right font-weight-bold" style="border-top: 1px solid #333;">{{ deliveries.actual_deliveries_total_sale }}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    <div class="card-body text-danger" v-else><i>Ingen data...</i></div>
                </div>
            </div>
            <div class="col-xl-6">
                <div class="card">
                    <div class="card-header bg-white">
                        <h5 class="card-title">Manuella saldoändringar {{ year }}</h5>
                    </div>
                    <div v-if="stock_adjustments || busy">
                        <table class="table table-xs">
                            <thead>
                                <tr>
                                    <th>Datum</th>
                                    <th>Ändring</th>
                                    <th>Kommentar</th>
                                    <th>Användare</th>
                                </tr>
                            </thead>
                            <tbody v-if="busy">
                                <tr>
                                    <td colspan="4" class="text-center py-5"><i class="icon-spinner2 spinner"></i></td>
                                </tr>
                            </tbody>
                            <tbody v-else-if="stock_adjustments.length > 0">
                                <tr v-for="(data, sa_index) in stock_adjustments" :key="'sa-' + sa_index">
                                    <td>{{ formatDateTime(data.CreatedTS) }} (W{{ data.vecka }})</td>
                                    <td>{{ data.Antal }}</td>
                                    <td>{{ data.Kommentar }}</td>
                                    <td>{{ (data.FullName ? data.FullName : '') }}</td>
                                </tr>
                            </tbody>
                            <tbody v-else>
                                <tr>
                                    <td colspan="4" class="text-center py-5"><i>Ingen data...</i></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="card-body text-danger" v-else><i>Ingen data...</i></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import apiClient from '@services/ApiClient';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart } from 'echarts/charts';
import {
    TitleComponent,
    TooltipComponent,
    GridComponent,
    ToolboxComponent,
} from 'echarts/components';
import VChart from 'vue-echarts';
import { formatDateTime } from '@services/Helpers';

use([
    CanvasRenderer,
    TitleComponent,
    TooltipComponent,
    GridComponent,
    ToolboxComponent,
    LineChart,
]);

export default {
    name: 'ProductEditorDeliveryStatistics',
    props: {
        id: {
            default: 'new',
        },
    },
    components: {
        VChart,
    },
    created() {
        const today = new Date();
        this.year = today.getFullYear();
    },
    data: () => ({
        busy: true,
        deliveries: null,
        stock_adjustments: null,
        year: null,
    }),
    watch: {
        year() {
            this.getDeliveryStatistics();
            this.getStockChanges();
        },
    },
    methods: {
        formatDateTime,
        async getDeliveryStatistics() {
            this.busy = true;
            await apiClient.get('/vue/products/' + this.id + '/delivery_statistics?year=' + this.year)
                .then((response) => {
                    if (response.data) {
                        this.deliveries = response.data;
                    }
                })
                .finally(() => {
                    this.busy = false;
                });
        },
        async getStockChanges() {
            this.busy = true;
            await apiClient.get('/vue/products/' + this.id + '/stock_adjustments?year=' + this.year)
                .then((response) => {
                    if (response.data) {
                        this.stock_adjustments = response.data;
                    }
                })
                .finally(() => {
                    this.busy = false;
                });
        },
    },
    computed: {
        old_system_url() {
            return window.config.app.old_system_url;
        },
        years() {
            const arr = [];
            const today = new Date();
            const year = today.getFullYear();
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < 10; i++) {
                arr.push(year - i);
            }
            return arr;
        },
        chart_options() {
            const opt = {
                // title: {
                //     text: 'Traffic Sources',
                //     left: 'center',
                // },
                grid: {
                    top: 50,
                    bottom: 40,
                },
                tooltip: {
                    trigger: 'axis',
                },
                xAxis: {
                    type: 'category',
                    name: 'Vecka',
                    data: [],
                },
                yAxis: {
                    type: 'value',
                    position: 'left',
                    name: 'Antal',
                },
                series: [
                    {
                        type: 'line',
                        name: 'Inlösen',
                        data: [],
                    },
                    {
                        type: 'line',
                        name: 'Sälj',
                        data: [],
                    },
                ],
            };

            if (this.deliveries && this.deliveries.actual_deliveries) {
                Object.entries(this.deliveries.actual_deliveries).forEach(([key, data]) => {
                    opt.xAxis.data.push(key);
                    opt.series[0].data.push(parseInt(data.levstat, 10));
                    opt.series[1].data.push(parseInt(data.salestat, 10));
                });
            }

            return opt;
        },
    },
};
</script>
